import { useEffect, useState } from 'react';
import api from '@/api';
import { useAuth } from '@/contexts/AuthContext';
import { MessageSquare, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function Messages() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [composing, setComposing] = useState(false);
  const [form, setForm] = useState({ subject: '', content: '' });

  const load = async () => {
    const r = await api.get('/messages/');
    setMessages(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const markRead = async (msg) => {
    setSelected(msg);
    if (!msg.is_read && msg.receiver_id === user._id) {
      await api.put(`/messages/${msg._id}/read`);
      setMessages(prev => prev.map(m => m._id === msg._id ? { ...m, is_read: true } : m));
    }
  };

  const unread = messages.filter(m => !m.is_read && m.receiver_id === user?._id).length;

  return (
    <div className="space-y-5" data-testid="messages-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Messages
            {unread > 0 && <span className="ml-2 text-sm bg-red-500 text-white px-2 py-0.5 rounded-full">{unread}</span>}
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Your inbox and sent messages</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-white rounded-xl border animate-pulse" />)}</div>
      ) : messages.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No messages yet</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
          {messages.map(msg => (
            <button
              key={msg._id}
              onClick={() => markRead(msg)}
              data-testid={`message-${msg._id}`}
              className={`w-full text-left px-5 py-4 hover:bg-slate-50 transition-colors flex items-start gap-4 ${!msg.is_read && msg.receiver_id === user?._id ? 'bg-blue-50/30' : ''}`}
            >
              <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${!msg.is_read && msg.receiver_id === user?._id ? 'bg-blue-500' : 'bg-transparent'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className={`text-sm ${!msg.is_read && msg.receiver_id === user?._id ? 'font-bold text-slate-900' : 'font-semibold text-slate-700'} truncate`}>
                    {msg.subject}
                  </p>
                  <p className="text-xs text-slate-400 flex-shrink-0">{msg.created_at ? new Date(msg.created_at).toLocaleDateString() : ''}</p>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">From: {msg.sender_name}</p>
                <p className="text-sm text-slate-500 mt-1 line-clamp-1">{msg.content}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Message Detail */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        {selected && (
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">{selected.subject}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex justify-between text-xs text-slate-500">
                <span>From: <span className="font-semibold">{selected.sender_name}</span></span>
                <span>{selected.created_at ? new Date(selected.created_at).toLocaleString() : ''}</span>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{selected.content}</p>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
